import React, { useState } from 'react';
import { listarMovimentacoes, inserirMovimentacao, atualizarMovimentacao, excluirMovimentacao, listarMotos, fmtMoeda, fmtData } from '../lib/supabase';
import {
  Plus, Trash2, Edit2, X, TrendingUp, TrendingDown, DollarSign, ChevronDown, ChevronUp,
  Filter, AlertCircle, BarChart3, Calendar, Download, Bike, Wrench, Landmark, Activity,
  MoreHorizontal, LayoutGrid, ArrowUp, ArrowDown,
} from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from 'recharts';

const CATS_ENTRADA = ['Venda de Moto', 'Outros'];
const CATS_SAIDA   = ['Entrada de Moto', 'Despesa com Moto', 'Custo Variável', 'Custo Fixo', 'Outros'];
const CATS_MOTO_OBRIG = ['Despesa com Moto', 'Entrada de Moto'];

const FORM_VAZIO = { tipo: 'Entrada', categoria: CATS_ENTRADA[0], descricao: '', valor: '', data: new Date().toISOString().slice(0,10), moto_id: '' };

const CORES_SAIDA   = { 'Entrada de Moto': '#3B82F6', 'Despesa com Moto': '#22C55E', 'Custo Fixo': '#F59E0B', 'Custo Variável': '#8B5CF6', 'Outros': '#9CA3AF' };
const CORES_ENTRADA = { 'Venda de Moto': '#22C55E', 'Outros': '#3B82F6' };

const TABS = [
  { id: 'visao-geral',    label: 'Visão Geral',       Icon: LayoutGrid },
  { id: 'entradas',       label: 'Entradas',          Icon: TrendingUp },
  { id: 'saidas',         label: 'Saídas',             Icon: TrendingDown },
  { id: 'entrada-moto',   label: 'Entrada de Moto',    Icon: Bike,       categoria: 'Entrada de Moto',   grouped: true },
  { id: 'despesa-moto',   label: 'Despesa com Moto',   Icon: Wrench,     categoria: 'Despesa com Moto',  grouped: true },
  { id: 'custo-fixo',     label: 'Custo Fixo',         Icon: Landmark,   categoria: 'Custo Fixo' },
  { id: 'custo-variavel', label: 'Custo Variável',     Icon: Activity,   categoria: 'Custo Variável' },
  { id: 'outros',         label: 'Outros',             Icon: MoreHorizontal, categoria: 'Outros' },
];

// Normaliza o campo tipo vindo do banco: tira espaços, acento e case,
// para não depender de o valor estar gravado como "Saída" byte-a-byte.
function ehSaida(tipo) {
  const t = (tipo || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
  return t === 'saida';
}
function ehEntrada(tipo) {
  const t = (tipo || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
  return t === 'entrada';
}

function mesAbrev(dateStr) {
  const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  const d = new Date(dateStr + 'T00:00:00');
  return meses[d.getMonth()];
}

function fmtMesLabel(key) {
  const meses = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
  const [ano, mes] = key.split('-').map(Number);
  return `${meses[mes-1]}/${ano}`;
}

function diasEntre(a, b) {
  const da = new Date(a + 'T00:00:00'), db = new Date(b + 'T00:00:00');
  return Math.round((db - da) / 86400000);
}

function somarDias(dataStr, dias) {
  const d = new Date(dataStr + 'T00:00:00');
  d.setDate(d.getDate() + dias);
  return d.toISOString().slice(0,10);
}

function variacaoPct(atual, anterior) {
  if (!anterior) return atual > 0 ? 100 : atual < 0 ? -100 : 0;
  return ((atual - anterior) / Math.abs(anterior)) * 100;
}

function DonutChart({ data, cores, total }) {
  if (!data.length) {
    return (
      <div className="donut-vazio">
        <BarChart3 size={32} strokeWidth={1} color="#CBD5E1" />
        <p>Sem dados no período.</p>
      </div>
    );
  }
  return (
    <div className="donut-wrap">
      <div className="donut-chart-area">
        <ResponsiveContainer width="100%" height={170}>
          <PieChart>
            <Pie data={data} dataKey="valor" nameKey="categoria" innerRadius={46} outerRadius={72} paddingAngle={2} stroke="none">
              {data.map((d, i) => <Cell key={i} fill={cores[d.categoria] || '#CBD5E1'} />)}
            </Pie>
            <Tooltip formatter={v => fmtMoeda(v)} />
          </PieChart>
        </ResponsiveContainer>
        <div className="donut-center">
          <span className="donut-center-valor">{fmtMoeda(total)}</span>
          <span className="donut-center-label">Total</span>
        </div>
      </div>
      <div className="donut-legend">
        {data.map((d, i) => (
          <div key={i} className="donut-legend-item">
            <span className="donut-dot" style={{ background: cores[d.categoria] || '#CBD5E1' }} />
            <span className="donut-legend-nome">{d.categoria}</span>
            <span className="donut-legend-pct">{total ? ((d.valor / total) * 100).toFixed(1) : '0.0'}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Financeiro() {
  const [movs,        setMovs]       = useState([]);
  const [motos,       setMotos]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [aba,         setAba]         = useState('visao-geral');
  const [modalAberto, setModalAberto] = useState(false);
  const [form,        setForm]        = useState(FORM_VAZIO);
  const [salvando,    setSalvando]    = useState(false);
  const [editando,    setEditando]    = useState(null);
  const [excluindo,   setExcluindo]   = useState(null);
  const [filtroMes,   setFiltroMes]   = useState('todos');

  const hoje = new Date().toISOString().slice(0,10);
  const primeiroDiaMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0,10);
  const [dataInicio,  setDataInicio]  = useState(primeiroDiaMes);
  const [dataFim,      setDataFim]     = useState(hoje);
  const [dateOpen,     setDateOpen]    = useState(false);

  const [motoSelecionadaId, setMotoSelecionadaId] = useState(null);
  const [subTab,          setSubTab]              = useState('despesas');
  const [verTodasMotos,   setVerTodasMotos]        = useState(false);

  const carregar = async () => {
    setLoading(true);
    const [m, mt] = await Promise.all([listarMovimentacoes(), listarMotos()]);
    setMovs(m || []); setMotos(mt || []);
    setLoading(false);
  };

  React.useEffect(() => { carregar(); }, []);

  const mesesDisponiveis = [...new Set(movs.map(m => m.data?.slice(0,7)))].filter(Boolean).sort().reverse();

  const aplicarFiltroMes = (val) => {
    setFiltroMes(val);
    if (val === 'todos') {
      const datas = movs.map(m => m.data).filter(Boolean).sort();
      setDataInicio(datas[0] || primeiroDiaMes);
      setDataFim(hoje);
    } else {
      const [ano, mes] = val.split('-').map(Number);
      const ini = new Date(ano, mes - 1, 1);
      const fim = new Date(ano, mes, 0);
      setDataInicio(ini.toISOString().slice(0,10));
      setDataFim(fim.toISOString().slice(0,10));
    }
  };

  // ── Período atual x período anterior (mesma duração, imediatamente antes) ──
  const movsPeriodo = movs.filter(m => m.data >= dataInicio && m.data <= dataFim);
  const duracao = diasEntre(dataInicio, dataFim) + 1;
  const anteriorFim = somarDias(dataInicio, -1);
  const anteriorInicio = somarDias(anteriorFim, -(duracao - 1));
  const movsAnterior = movs.filter(m => m.data >= anteriorInicio && m.data <= anteriorFim);

  const totalEntradas = movsPeriodo.filter(m => ehEntrada(m.tipo)).reduce((s,m) => s + Number(m.valor||0), 0);
  const totalSaidas   = movsPeriodo.filter(m => ehSaida(m.tipo)).reduce((s,m) => s + Number(m.valor||0), 0);
  const saldo         = totalEntradas - totalSaidas;

  const totalEntradasAnt = movsAnterior.filter(m => ehEntrada(m.tipo)).reduce((s,m) => s + Number(m.valor||0), 0);
  const totalSaidasAnt   = movsAnterior.filter(m => ehSaida(m.tipo)).reduce((s,m) => s + Number(m.valor||0), 0);
  const saldoAnt         = totalEntradasAnt - totalSaidasAnt;

  const varEntradas = variacaoPct(totalEntradas, totalEntradasAnt);
  const varSaidas   = variacaoPct(totalSaidas, totalSaidasAnt);
  const varSaldo    = variacaoPct(saldo, saldoAnt);

  const countEntradas = movsPeriodo.filter(m => ehEntrada(m.tipo)).length;
  const countSaidas   = movsPeriodo.filter(m => ehSaida(m.tipo)).length;

  // ── Fluxo de caixa: últimos 6 meses, com saldo acumulado (independe do período selecionado) ──
  const graficoFluxo = (() => {
    const agora = new Date();
    const inicioJanela = new Date(agora.getFullYear(), agora.getMonth() - 5, 1);
    const antes = movs.filter(m => m.data && new Date(m.data + 'T00:00:00') < inicioJanela);
    let saldoAcum = antes.reduce((s,m) => s + (ehEntrada(m.tipo) ? Number(m.valor||0) : -Number(m.valor||0)), 0);
    return Array.from({length:6}, (_,i) => {
      const d = new Date(agora.getFullYear(), agora.getMonth() - (5-i), 1);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      const ent = movs.filter(m => ehEntrada(m.tipo) && m.data?.startsWith(key)).reduce((s,m)=>s+Number(m.valor||0),0);
      const sai = movs.filter(m => ehSaida(m.tipo)   && m.data?.startsWith(key)).reduce((s,m)=>s+Number(m.valor||0),0);
      saldoAcum += ent - sai;
      return { mes: mesAbrev(key+'-01'), Entradas: ent, Saídas: sai, Saldo: saldoAcum };
    });
  })();

  const saidasPorCategoria = (() => {
    const map = {};
    movsPeriodo.filter(m => ehSaida(m.tipo)).forEach(m => {
      const c = m.categoria || 'Outros';
      map[c] = (map[c] || 0) + Number(m.valor || 0);
    });
    return Object.entries(map).map(([categoria, valor]) => ({ categoria, valor })).sort((a,b) => b.valor - a.valor);
  })();

  const entradasPorCategoria = (() => {
    const map = {};
    movsPeriodo.filter(m => ehEntrada(m.tipo)).forEach(m => {
      const c = m.categoria || 'Outros';
      map[c] = (map[c] || 0) + Number(m.valor || 0);
    });
    return Object.entries(map).map(([categoria, valor]) => ({ categoria, valor })).sort((a,b) => b.valor - a.valor);
  })();

  const handleField = (e) => {
    const { name, value } = e.target;
    if (name === 'tipo') {
      const cat = value === 'Entrada' ? CATS_ENTRADA[0] : CATS_SAIDA[0];
      return setForm(f => ({ ...f, tipo: value, categoria: cat, moto_id: '' }));
    }
    if (name === 'categoria') {
      return setForm(f => ({ ...f, categoria: value, moto_id: '' }));
    }
    setForm(f => ({ ...f, [name]: value }));
  };

  const motoObrigatorio = CATS_MOTO_OBRIG.includes(form.categoria);
  const cats = form.tipo === 'Entrada' ? CATS_ENTRADA : CATS_SAIDA;

  const abrirNovo = () => { setEditando(null); setForm(FORM_VAZIO); setModalAberto(true); };

  const abrirEditar = (m) => {
    setEditando(m.id);
    setForm({
      tipo: ehEntrada(m.tipo) ? 'Entrada' : 'Saída',
      categoria: m.categoria || (ehEntrada(m.tipo) ? CATS_ENTRADA[0] : CATS_SAIDA[0]),
      descricao: m.descricao || '',
      valor: m.valor != null ? String(m.valor) : '',
      data: m.data || new Date().toISOString().slice(0,10),
      moto_id: m.moto_id || '',
    });
    setModalAberto(true);
  };

  const fecharModal = () => { setModalAberto(false); setEditando(null); setForm(FORM_VAZIO); };

  const salvar = async () => {
    if (!form.descricao || !form.valor || !form.data) return alert('Preencha descrição, valor e data.');
    if (motoObrigatorio && !form.moto_id) return alert(`Para a categoria "${form.categoria}" é obrigatório selecionar a moto.`);
    setSalvando(true);
    try {
      const payload = {
        tipo: form.tipo, categoria: form.categoria, descricao: form.descricao,
        valor: parseFloat(form.valor), data: form.data,
        moto_id: form.moto_id ? parseInt(form.moto_id) : null,
      };
      if (editando) {
        await atualizarMovimentacao(editando, payload);
      } else {
        await inserirMovimentacao(payload);
      }
      await carregar();
      fecharModal();
    } catch (err) { alert('Erro: ' + (err.message || err)); }
    finally { setSalvando(false); }
  };

  const confirmarExcluir = async (id) => {
    if (!window.confirm('Excluir esta movimentação?')) return;
    setExcluindo(id);
    await excluirMovimentacao(id);
    await carregar();
    setExcluindo(null);
  };

  const exportarRelatorio = () => {
    const linhas = [['Data','Tipo','Categoria','Descrição','Moto','Valor']];
    movsPeriodo.forEach(m => {
      const moto = motos.find(x => x.id === m.moto_id);
      linhas.push([
        fmtData(m.data), m.tipo, m.categoria || '',
        (m.descricao || '').replace(/,/g, ';'),
        moto ? `${moto.marca} ${moto.modelo}` : '',
        String(m.valor || 0).replace('.', ','),
      ]);
    });
    const csv = linhas.map(l => l.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `relatorio-financeiro_${dataInicio}_a_${dataFim}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const fmtK = (v) => v >= 1000 ? `R$${(v/1000).toFixed(1)}k` : `R$${v.toFixed(0)}`;

  const abaAtual = TABS.find(t => t.id === aba) || TABS[0];

  const gruposPorMoto = (categoria) => {
    const map = {};
    movsPeriodo.filter(m => m.categoria === categoria && m.moto_id).forEach(m => {
      if (!map[m.moto_id]) map[m.moto_id] = { moto_id: m.moto_id, moto: motos.find(x => x.id === m.moto_id), total: 0, data: m.data };
      map[m.moto_id].total += Number(m.valor || 0);
      if (m.data > map[m.moto_id].data) map[m.moto_id].data = m.data;
    });
    return Object.values(map).sort((a,b) => (b.data||'').localeCompare(a.data||''));
  };

  const gruposAtuais = abaAtual.grouped ? gruposPorMoto(abaAtual.categoria) : [];
  const motoAtivoId = abaAtual.grouped
    ? (gruposAtuais.find(g => g.moto_id === motoSelecionadaId) ? motoSelecionadaId : gruposAtuais[0]?.moto_id ?? null)
    : null;
  const motoDetalhe = motos.find(x => x.id === motoAtivoId);
  const entriesMoto = movsPeriodo.filter(m => m.moto_id === motoAtivoId).sort((a,b) => (b.data||'').localeCompare(a.data||''));
  const valorEntradaMoto   = entriesMoto.filter(m => m.categoria === 'Entrada de Moto').reduce((s,m) => s + Number(m.valor||0), 0);
  const totalDespesasMoto  = entriesMoto.filter(m => m.categoria === 'Despesa com Moto').reduce((s,m) => s + Number(m.valor||0), 0);
  const totalInvestidoMoto = valorEntradaMoto + totalDespesasMoto;

  const listaAtual = abaAtual.grouped ? [] : (
    aba === 'visao-geral' ? movsPeriodo :
    aba === 'entradas'    ? movsPeriodo.filter(m => ehEntrada(m.tipo)) :
    aba === 'saidas'      ? movsPeriodo.filter(m => ehSaida(m.tipo)) :
    movsPeriodo.filter(m => m.categoria === abaAtual.categoria)
  );

  return (
    <div className="page">
      <div className="fin-banner">
        <div>
          <h1 className="fin-banner-title">Financeiro</h1>
          <p className="fin-banner-sub">Relatórios e análise completa</p>
        </div>
        <div className="fin-banner-actions">
          <div className="date-range-wrap">
            <button className="date-range-btn" onClick={() => setDateOpen(v => !v)}>
              <Calendar size={15} /> {fmtData(dataInicio)} - {fmtData(dataFim)} <ChevronDown size={14} />
            </button>
            {dateOpen && (
              <div className="date-range-pop" onClick={e => e.stopPropagation()}>
                <div className="form-group">
                  <label className="label">De</label>
                  <input className="input-field" type="date" value={dataInicio} onChange={e => { setFiltroMes('todos'); setDataInicio(e.target.value); }} />
                </div>
                <div className="form-group">
                  <label className="label">Até</label>
                  <input className="input-field" type="date" value={dataFim} onChange={e => { setFiltroMes('todos'); setDataFim(e.target.value); }} />
                </div>
                <button className="btn btn-primary btn-sm" style={{width:'100%',justifyContent:'center'}} onClick={() => setDateOpen(false)}>Aplicar</button>
              </div>
            )}
          </div>
          <button className="btn fin-export-btn" onClick={exportarRelatorio}><Download size={16} /> Exportar Relatório</button>
        </div>
      </div>

      <div className="fin-kpis fin-kpis-4">
        <div className="fin-kpi fin-kpi--verde">
          <div className="fin-kpi-icon"><TrendingUp size={20} /></div>
          <div>
            <p className="fin-kpi-label">Total Entradas</p>
            <p className="fin-kpi-valor">{fmtMoeda(totalEntradas)}</p>
            <p className={`fin-kpi-var ${varEntradas>=0?'up':'down'}`}>{varEntradas>=0?<ArrowUp size={11}/>:<ArrowDown size={11}/>} {Math.abs(varEntradas).toFixed(1)}% vs período anterior</p>
          </div>
        </div>
        <div className="fin-kpi fin-kpi--vermelho">
          <div className="fin-kpi-icon"><TrendingDown size={20} /></div>
          <div>
            <p className="fin-kpi-label">Total Saídas</p>
            <p className="fin-kpi-valor">{fmtMoeda(totalSaidas)}</p>
            <p className={`fin-kpi-var ${varSaidas<=0?'up':'down'}`}>{varSaidas>=0?<ArrowUp size={11}/>:<ArrowDown size={11}/>} {Math.abs(varSaidas).toFixed(1)}% vs período anterior</p>
          </div>
        </div>
        <div className={`fin-kpi ${saldo >= 0 ? 'fin-kpi--azul' : 'fin-kpi--laranja'}`}>
          <div className="fin-kpi-icon"><DollarSign size={20} /></div>
          <div>
            <p className="fin-kpi-label">Saldo Líquido</p>
            <p className="fin-kpi-valor">{fmtMoeda(saldo)}</p>
            <p className={`fin-kpi-var ${varSaldo>=0?'up':'down'}`}>{varSaldo>=0?<ArrowUp size={11}/>:<ArrowDown size={11}/>} {Math.abs(varSaldo).toFixed(1)}% vs período anterior</p>
          </div>
        </div>
        <div className="fin-kpi fin-kpi--azul">
          <div className="fin-kpi-icon"><BarChart3 size={20} /></div>
          <div>
            <p className="fin-kpi-label">Total Lançamentos</p>
            <p className="fin-kpi-valor">{movsPeriodo.length}</p>
            <p className="fin-kpi-sub">{countEntradas} entradas e {countSaidas} saídas</p>
          </div>
        </div>
      </div>

      <div className="fin-charts-row">
        <div className="card fin-chart-card">
          <h3 className="card-titulo">Fluxo de Caixa <span className="card-subtitulo">(últimos 6 meses)</span></h3>
          <ResponsiveContainer width="100%" height={230}>
            <ComposedChart data={graficoFluxo} barCategoryGap="35%">
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="mes" tick={{fontSize:12,fill:'#9CA3AF'}} axisLine={false} tickLine={false} />
              <YAxis tick={{fontSize:11,fill:'#9CA3AF'}} axisLine={false} tickLine={false} tickFormatter={fmtK} />
              <Tooltip formatter={v => fmtMoeda(v)} />
              <Legend wrapperStyle={{fontSize:12}} />
              <Bar dataKey="Entradas" fill="#22C55E" radius={[4,4,0,0]} barSize={14} />
              <Bar dataKey="Saídas"   fill="#EF4444" radius={[4,4,0,0]} barSize={14} />
              <Line type="monotone" dataKey="Saldo" stroke="#3B82F6" strokeWidth={2} dot={{r:4,fill:'#3B82F6'}} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="card fin-donut-card">
          <h3 className="card-titulo">Saídas por Categoria</h3>
          <DonutChart data={saidasPorCategoria} cores={CORES_SAIDA} total={totalSaidas} />
        </div>
        <div className="card fin-donut-card">
          <h3 className="card-titulo">Entradas por Categoria</h3>
          <DonutChart data={entradasPorCategoria} cores={CORES_ENTRADA} total={totalEntradas} />
        </div>
      </div>

      <div className="fin-toolbar">
        <div className="abas abas-wrap">
          {TABS.map(t => (
            <button key={t.id} className={`aba ${aba===t.id?'aba--ativa':''}`} onClick={() => setAba(t.id)}>
              <t.Icon size={14} /> {t.label}
            </button>
          ))}
        </div>
        <div className="fin-toolbar-right">
          <button className="btn btn-ghost btn-sm" onClick={() => setDateOpen(v => !v)}><Filter size={13} /> Filtros</button>
          <div className="select-wrap">
            <select className="filter-select" value={filtroMes} onChange={e => aplicarFiltroMes(e.target.value)}>
              <option value="todos">Todos os meses</option>
              {mesesDisponiveis.map(m => <option key={m} value={m}>{fmtMesLabel(m)}</option>)}
            </select>
            <ChevronDown size={13} className="select-arrow" />
          </div>
          <button className="btn btn-primary btn-sm" onClick={abrirNovo}><Plus size={14} /> Nova</button>
        </div>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : abaAtual.grouped ? (
        <div className="moto-grouped">
          <div className="moto-lista-col">
            <div className="moto-lista-header">
              <h3>{aba==='entrada-moto' ? 'Entrada de Motos' : 'Despesas com Motos'}</h3>
              <p>{aba==='entrada-moto' ? 'Motos adquiridas no período' : 'Motos com despesas no período'}</p>
            </div>
            {gruposAtuais.length === 0 ? (
              <div className="empty-state" style={{padding:'32px 12px'}}>
                <Bike size={40} strokeWidth={1} color="#CBD5E1" />
                <p>Nenhum registro no período.</p>
              </div>
            ) : (
              <>
                <div className="moto-lista-items">
                  {(verTodasMotos ? gruposAtuais : gruposAtuais.slice(0,4)).map(g => (
                    <button key={g.moto_id} className={`moto-item ${motoAtivoId===g.moto_id?'moto-item--ativa':''}`} onClick={() => { setMotoSelecionadaId(g.moto_id); setSubTab('despesas'); }}>
                      <div className="moto-item-icon"><Bike size={20} /></div>
                      <div className="moto-item-info">
                        <p className="moto-item-nome">{g.moto ? `${g.moto.marca||''} ${g.moto.modelo||''}${g.moto.ano?' '+g.moto.ano:''}`.trim() : 'Moto removida'}</p>
                        <p className="moto-item-meta">Placa: {g.moto?.placa || '—'}</p>
                        <p className="moto-item-meta">Data entrada: {fmtData(g.data)}</p>
                      </div>
                      <div className="moto-item-valor">-{fmtMoeda(g.total)}</div>
                    </button>
                  ))}
                </div>
                {gruposAtuais.length > 4 && (
                  <button className="btn btn-ghost btn-sm ver-todas-btn" onClick={() => setVerTodasMotos(v => !v)}>
                    {verTodasMotos ? 'Ver menos' : `Ver todas as motos (${gruposAtuais.length})`}
                  </button>
                )}
              </>
            )}
          </div>

          <div className="moto-detail-col card">
            {!motoDetalhe ? (
              <div className="empty-state"><Bike size={40} strokeWidth={1} color="#CBD5E1" /><p>Selecione um item na lista ao lado.</p></div>
            ) : (
              <>
                <div className="detail-header">
                  <h3>
                    {motoDetalhe.marca} {motoDetalhe.modelo}{motoDetalhe.ano ? ` ${motoDetalhe.ano}` : ''}
                    {motoDetalhe.placa && <span className="badge badge-azul" style={{marginLeft:8,verticalAlign:'middle'}}>{motoDetalhe.placa}</span>}
                  </h3>
                  <div className="detail-total">
                    <span className="detail-total-label">Total investido</span>
                    <span className="detail-total-valor">-{fmtMoeda(Math.abs(totalInvestidoMoto))}</span>
                  </div>
                </div>

                <div className="detail-subtabs">
                  {['detalhes','despesas','relacionados','historico'].map(st => (
                    <button key={st} className={`detail-subtab ${subTab===st?'ativa':''}`} onClick={() => setSubTab(st)}>
                      {st==='detalhes'?'Detalhes':st==='despesas'?'Despesas':st==='relacionados'?'Lançamentos relacionados':'Histórico'}
                    </button>
                  ))}
                </div>

                <div className="detail-body">
                  {subTab === 'detalhes' && (
                    <div className="detalhes-grid">
                      <div><span className="dl-label">Marca</span><span className="dl-valor">{motoDetalhe.marca || '—'}</span></div>
                      <div><span className="dl-label">Modelo</span><span className="dl-valor">{motoDetalhe.modelo || '—'}</span></div>
                      <div><span className="dl-label">Ano</span><span className="dl-valor">{motoDetalhe.ano || '—'}</span></div>
                      <div><span className="dl-label">Placa</span><span className="dl-valor">{motoDetalhe.placa || '—'}</span></div>
                      <div><span className="dl-label">Cor</span><span className="dl-valor">{motoDetalhe.cor || '—'}</span></div>
                      <div><span className="dl-label">Status</span><span className="dl-valor">{motoDetalhe.status || '—'}</span></div>
                      <div><span className="dl-label">Km entrada</span><span className="dl-valor">{motoDetalhe.km_entrada || '—'}</span></div>
                      <div><span className="dl-label">Data entrada</span><span className="dl-valor">{fmtData(motoDetalhe.data_entrada) || '—'}</span></div>
                    </div>
                  )}

                  {subTab === 'despesas' && (
                    entriesMoto.length === 0 ? (
                      <div className="empty-state" style={{padding:'24px 0'}}><p>Nenhum lançamento no período.</p></div>
                    ) : (
                      <div className="detail-table-wrap">
                        <table className="detail-table">
                          <thead><tr><th>Data</th><th>Descrição</th><th>Categoria</th><th style={{textAlign:'right'}}>Valor</th></tr></thead>
                          <tbody>
                            {entriesMoto.map(m => (
                              <tr key={m.id}>
                                <td>{fmtData(m.data)}</td>
                                <td>{m.descricao}</td>
                                <td>{m.categoria}</td>
                                <td className="vermelho" style={{textAlign:'right',fontWeight:600}}>-{fmtMoeda(m.valor)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="detail-table-total">
                          <span>Total de despesas</span>
                          <span className="vermelho">-{fmtMoeda(Math.abs(totalDespesasMoto))}</span>
                        </div>
                      </div>
                    )
                  )}

                  {subTab === 'relacionados' && (
                    entriesMoto.length === 0 ? (
                      <div className="empty-state" style={{padding:'24px 0'}}><p>Nenhum lançamento relacionado.</p></div>
                    ) : (
                      <div className="relatorio-lista">
                        {Object.values(entriesMoto.reduce((acc,m) => {
                          const c = m.categoria || 'Outros';
                          if (!acc[c]) acc[c] = { categoria:c, total:0, count:0 };
                          acc[c].total += Number(m.valor||0); acc[c].count += 1;
                          return acc;
                        }, {})).map(r => (
                          <div key={r.categoria} className="relatorio-item">
                            <div className="relatorio-info">
                              <p className="relatorio-cat">{r.categoria}</p>
                              <p className="relatorio-count">{r.count} lançamento{r.count!==1?'s':''}</p>
                            </div>
                            <div className="relatorio-valores"><span className="relatorio-valor vermelho">-{fmtMoeda(r.total)}</span></div>
                          </div>
                        ))}
                      </div>
                    )
                  )}

                  {subTab === 'historico' && (
                    entriesMoto.length === 0 ? (
                      <div className="empty-state" style={{padding:'24px 0'}}><p>Sem histórico no período.</p></div>
                    ) : (
                      <div className="timeline">
                        {[...entriesMoto].reverse().map(m => (
                          <div key={m.id} className="timeline-item">
                            <span className="timeline-dot" />
                            <div className="timeline-content">
                              <p className="timeline-desc">{m.descricao} <span className="timeline-valor vermelho">-{fmtMoeda(m.valor)}</span></p>
                              <p className="timeline-meta">{fmtData(m.data)} · {m.categoria}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>

                {subTab === 'despesas' && (
                  <div className="detail-footer-3col">
                    <div><span className="dl-label">Valor de entrada</span><span className="dl-valor vermelho">-{fmtMoeda(Math.abs(valorEntradaMoto))}</span></div>
                    <div><span className="dl-label">Total despesas</span><span className="dl-valor vermelho">-{fmtMoeda(Math.abs(totalDespesasMoto))}</span></div>
                    <div><span className="dl-label">Total investido</span><span className="dl-valor vermelho">-{fmtMoeda(Math.abs(totalInvestidoMoto))}</span></div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : listaAtual.length === 0 ? (
        <div className="empty-state">
          <DollarSign size={48} strokeWidth={1} color="#CBD5E1" />
          <p>Nenhuma movimentação encontrada.</p>
          <button className="btn btn-primary" onClick={abrirNovo}><Plus size={16} /> Adicionar</button>
        </div>
      ) : (
        <div className="movs-lista">
          {listaAtual.map(m => (
            <div key={m.id} className="mov-item">
              <div className={`mov-tipo-icon ${ehEntrada(m.tipo)?'entrada':'saida'}`}>
                {ehEntrada(m.tipo)?<TrendingUp size={18}/>:<TrendingDown size={18}/>}
              </div>
              <div className="mov-info">
                <p className="mov-desc">{m.descricao}</p>
                <p className="mov-meta">
                  {m.categoria} · {fmtData(m.data)}
                  {m.motos && <span className="mov-moto"> · {m.motos.marca} {m.motos.modelo}</span>}
                </p>
              </div>
              <div className={`mov-valor ${ehEntrada(m.tipo)?'verde':'vermelho'}`}>
                {ehEntrada(m.tipo)?'+':'-'}{fmtMoeda(m.valor)}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => abrirEditar(m)}><Edit2 size={15}/></button>
              <button className="btn btn-ghost btn-sm btn-danger" onClick={() => confirmarExcluir(m.id)} disabled={excluindo===m.id}><Trash2 size={15}/></button>
            </div>
          ))}
        </div>
      )}

      {modalAberto && (
        <div className="modal-backdrop" onClick={fecharModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editando ? 'Editar Movimentação' : 'Nova Movimentação'}</h2>
              <button className="modal-close" onClick={fecharModal}><X size={20}/></button>
            </div>
            <div className="modal-body">
              <div className="tipo-toggle">
                {['Entrada','Saída'].map(t => (
                  <button key={t} type="button"
                    className={`tipo-btn ${form.tipo===t?(t==='Entrada'?'tipo-btn--entrada':'tipo-btn--saida'):''}`}
                    onClick={() => handleField({target:{name:'tipo',value:t}})}
                  >{t}</button>
                ))}
              </div>
              <div className="form-grid" style={{marginTop:16}}>
                <div className="form-group form-full">
                  <label className="label">Descrição *</label>
                  <input className="input-field" name="descricao" value={form.descricao} onChange={handleField} placeholder="Ex: Venda Honda CG 2022" />
                </div>
                <div className="form-group">
                  <label className="label">Categoria</label>
                  <div className="select-wrap">
                    <select className="input-field" name="categoria" value={form.categoria} onChange={handleField}>
                      {cats.map(c => <option key={c}>{c}</option>)}
                    </select>
                    <ChevronDown size={14} className="select-arrow" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="label">Data *</label>
                  <input className="input-field" name="data" type="date" value={form.data} onChange={handleField} />
                </div>
                <div className="form-group">
                  <label className="label">Valor (R$) *</label>
                  <input className="input-field" name="valor" type="number" step="0.01" value={form.valor} onChange={handleField} placeholder="0,00" />
                </div>
                <div className="form-group form-full">
                  <label className="label">
                    Moto vinculada {motoObrigatorio && <span className="obrigatorio">*obrigatório para "{form.categoria}"</span>}
                  </label>
                  {motoObrigatorio && (
                    <div className="alert-moto"><AlertCircle size={14}/> Selecione a moto para registrar o custo corretamente no histórico.</div>
                  )}
                  <div className="select-wrap">
                    <select className="input-field" name="moto_id" value={form.moto_id} onChange={handleField} style={motoObrigatorio&&!form.moto_id?{borderColor:'#EF4444'}:{}}>
                      <option value="">-- {motoObrigatorio ? 'Selecione a moto (obrigatório)' : 'Nenhuma'} --</option>
                      {motos.map(m => <option key={m.id} value={m.id}>{m.marca} {m.modelo} {m.ano} {m.placa?`· ${m.placa}`:''}</option>)}
                    </select>
                    <ChevronDown size={14} className="select-arrow" />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={fecharModal}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
                {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Registrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .page { padding: 24px; }

        .fin-banner { background:linear-gradient(135deg,var(--azul),var(--azul-m)); border-radius:16px; padding:22px 26px; display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:20px; flex-wrap:wrap; gap:14px; }
        .fin-banner-title { font-size:1.5rem; font-weight:700; color:#fff; margin:0; }
        .fin-banner-sub { color:rgba(255,255,255,.65); font-size:.85rem; margin:2px 0 0; }
        .fin-banner-actions { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
        .date-range-wrap { position:relative; }
        .date-range-btn { display:flex; align-items:center; gap:8px; background:rgba(255,255,255,.08); border:1.5px solid rgba(255,255,255,.18); color:#fff; padding:9px 14px; border-radius:10px; font-size:.85rem; font-weight:500; cursor:pointer; }
        .date-range-btn:hover { background:rgba(255,255,255,.14); }
        .date-range-pop { position:absolute; top:calc(100% + 8px); right:0; background:#fff; border-radius:12px; box-shadow:0 10px 30px rgba(0,0,0,.18); padding:16px; width:230px; z-index:50; display:flex; flex-direction:column; gap:10px; }
        .fin-export-btn { background:var(--amarelo); color:var(--azul); font-weight:700; }
        .fin-export-btn:hover { filter:brightness(0.95); }

        .fin-kpis { display:grid; grid-template-columns:repeat(3,1fr); gap:14px; margin-bottom:20px; }
        .fin-kpis-4 { grid-template-columns:repeat(4,1fr); }
        .fin-kpi { background:#fff; border-radius:12px; padding:16px; display:flex; align-items:flex-start; gap:12px; border:1.5px solid #E5E7EB; }
        .fin-kpi-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .fin-kpi--verde .fin-kpi-icon   { background:#DCFCE7; color:#16A34A; }
        .fin-kpi--vermelho .fin-kpi-icon { background:#FEE2E2; color:#DC2626; }
        .fin-kpi--azul .fin-kpi-icon    { background:#DBEAFE; color:var(--azul); }
        .fin-kpi--laranja .fin-kpi-icon  { background:#FEF3C7; color:#D97706; }
        .fin-kpi-label { font-size:.72rem; color:#6B7280; margin:0 0 3px; text-transform:uppercase; letter-spacing:.04em; font-weight:600; }
        .fin-kpi-valor { font-size:1.15rem; font-weight:700; color:var(--azul); margin:0; }
        .fin-kpi-var { display:flex; align-items:center; gap:3px; font-size:.7rem; font-weight:600; margin:4px 0 0; }
        .fin-kpi-var.up { color:#16A34A; }
        .fin-kpi-var.down { color:#DC2626; }
        .fin-kpi-sub { font-size:.7rem; color:#9CA3AF; margin:4px 0 0; }

        .fin-charts-row { display:grid; grid-template-columns:1.7fr 1fr 1fr; gap:16px; margin-bottom:20px; }
        .card-titulo { margin:0 0 14px; font-size:.92rem; font-weight:700; color:#1F2937; }
        .card-subtitulo { font-weight:500; color:#9CA3AF; font-size:.78rem; }

        .donut-wrap { display:flex; flex-direction:column; align-items:center; gap:10px; }
        .donut-chart-area { position:relative; width:100%; }
        .donut-center { position:absolute; top:50%; left:50%; transform:translate(-50%,-46%); text-align:center; pointer-events:none; }
        .donut-center-valor { display:block; font-size:.82rem; font-weight:700; color:var(--azul); white-space:nowrap; }
        .donut-center-label { display:block; font-size:.65rem; color:#9CA3AF; }
        .donut-legend { width:100%; display:flex; flex-direction:column; gap:6px; }
        .donut-legend-item { display:flex; align-items:center; gap:8px; font-size:.78rem; }
        .donut-dot { width:9px; height:9px; border-radius:50%; flex-shrink:0; }
        .donut-legend-nome { flex:1; color:#374151; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .donut-legend-pct { font-weight:700; color:#1F2937; }
        .donut-vazio { text-align:center; padding:24px 10px; color:#9CA3AF; display:flex; flex-direction:column; align-items:center; gap:8px; }
        .donut-vazio p { font-size:.8rem; margin:0; }

        .fin-toolbar { display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; flex-wrap:wrap; gap:10px; }
        .abas { display:flex; gap:4px; background:#F1F5F9; border-radius:8px; padding:4px; }
        .abas-wrap { flex-wrap:wrap; }
        .aba { display:flex; align-items:center; gap:6px; padding:7px 12px; border-radius:6px; border:none; background:transparent; font-size:.8rem; font-weight:500; color:#6B7280; cursor:pointer; transition:.15s; white-space:nowrap; }
        .aba--ativa { background:#fff; color:var(--azul); box-shadow:0 1px 4px rgba(0,0,0,.1); }
        .fin-toolbar-right { display:flex; align-items:center; gap:10px; }
        .select-wrap { position:relative; }
        .filter-select { padding:7px 28px 7px 10px; border:1.5px solid #E5E7EB; border-radius:8px; font-size:.85rem; outline:none; appearance:none; background:#fff; cursor:pointer; }
        .select-arrow { position:absolute; right:8px; top:50%; transform:translateY(-50%); pointer-events:none; color:#6B7280; }

        .movs-lista { display:flex; flex-direction:column; gap:8px; }
        .relatorio-lista { display:flex; flex-direction:column; gap:8px; }
        .relatorio-item { background:#fff; border-radius:10px; border:1.5px solid #E5E7EB; padding:14px 16px; display:flex; justify-content:space-between; align-items:center; gap:12px; }
        .relatorio-cat { font-weight:700; font-size:.92rem; color:#1F2937; margin:0 0 2px; }
        .relatorio-count { font-size:.78rem; color:#9CA3AF; margin:0; }
        .relatorio-valores { display:flex; flex-direction:column; align-items:flex-end; gap:2px; }
        .relatorio-valor { font-weight:700; font-size:.92rem; }
        .relatorio-valor.verde { color:#16A34A; }
        .relatorio-valor.vermelho { color:#DC2626; }
        .mov-item { background:#fff; border-radius:10px; border:1.5px solid #E5E7EB; padding:12px 14px; display:flex; align-items:center; gap:12px; }
        .mov-item:hover { border-color:#CBD5E1; }
        .mov-tipo-icon { width:38px; height:38px; border-radius:9px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .mov-tipo-icon.entrada { background:#DCFCE7; color:#16A34A; }
        .mov-tipo-icon.saida   { background:#FEE2E2; color:#DC2626; }
        .mov-info { flex:1; min-width:0; }
        .mov-desc { font-weight:600; font-size:.9rem; color:#1F2937; margin:0 0 2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .mov-meta { font-size:.78rem; color:#9CA3AF; margin:0; }
        .mov-moto { color:var(--azul); font-weight:500; }
        .mov-valor { font-weight:700; font-size:.95rem; flex-shrink:0; }
        .mov-valor.verde { color:#16A34A; }
        .mov-valor.vermelho { color:#DC2626; }
        .btn-ghost { background:transparent; border:1.5px solid #E5E7EB; color:#6B7280; }
        .btn-ghost:hover { background:#F9FAFB; }
        .btn-danger:hover { color:#EF4444 !important; border-color:#EF4444 !important; }

        .moto-grouped { display:grid; grid-template-columns:340px 1fr; gap:16px; align-items:start; }
        .moto-lista-col { background:#fff; border-radius:14px; border:1.5px solid #E5E7EB; padding:16px; }
        .moto-lista-header h3 { margin:0 0 2px; font-size:.95rem; font-weight:700; color:#1F2937; }
        .moto-lista-header p { margin:0 0 14px; font-size:.78rem; color:#9CA3AF; }
        .moto-lista-items { display:flex; flex-direction:column; gap:8px; }
        .moto-item { display:flex; align-items:center; gap:10px; text-align:left; background:#fff; border:1.5px solid #E5E7EB; border-radius:10px; padding:10px; cursor:pointer; width:100%; }
        .moto-item:hover { border-color:#CBD5E1; }
        .moto-item--ativa { border-color:var(--azul); background:#F0F5FF; }
        .moto-item-icon { width:40px; height:40px; border-radius:9px; background:#EEF2FF; color:var(--azul); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .moto-item-info { flex:1; min-width:0; }
        .moto-item-nome { font-weight:700; font-size:.85rem; color:#1F2937; margin:0 0 2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .moto-item-meta { font-size:.72rem; color:#9CA3AF; margin:0; }
        .moto-item-valor { font-weight:700; font-size:.85rem; color:#DC2626; flex-shrink:0; }
        .ver-todas-btn { width:100%; justify-content:center; margin-top:10px; }

        .moto-detail-col { min-height:400px; }
        .detail-header { display:flex; justify-content:space-between; align-items:flex-start; gap:12px; flex-wrap:wrap; margin-bottom:14px; }
        .detail-header h3 { margin:0; font-size:1.05rem; font-weight:700; color:#1F2937; }
        .detail-total { display:flex; flex-direction:column; align-items:flex-end; }
        .detail-total-label { font-size:.72rem; color:#9CA3AF; }
        .detail-total-valor { font-size:1rem; font-weight:700; color:#DC2626; }
        .detail-subtabs { display:flex; gap:18px; border-bottom:1.5px solid #F1F5F9; margin-bottom:16px; overflow-x:auto; }
        .detail-subtab { background:none; border:none; padding:8px 2px; font-size:.82rem; font-weight:600; color:#9CA3AF; cursor:pointer; border-bottom:2px solid transparent; white-space:nowrap; }
        .detail-subtab.ativa { color:var(--azul); border-bottom-color:var(--azul); }
        .detail-body { min-height:120px; }
        .detalhes-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(140px,1fr)); gap:14px; }
        .dl-label { display:block; font-size:.68rem; color:#9CA3AF; text-transform:uppercase; letter-spacing:.03em; margin-bottom:3px; }
        .dl-valor { display:block; font-size:.88rem; font-weight:600; color:#1F2937; }
        .dl-valor.vermelho { color:#DC2626; }
        .detail-table-wrap { overflow-x:auto; }
        .detail-table { width:100%; border-collapse:collapse; font-size:.82rem; }
        .detail-table th { text-align:left; color:#9CA3AF; font-weight:600; font-size:.72rem; text-transform:uppercase; letter-spacing:.03em; padding:8px 10px; border-bottom:1.5px solid #F1F5F9; }
        .detail-table td { padding:10px; border-bottom:1px solid #F8FAFC; color:#374151; }
        .detail-table-total { display:flex; justify-content:space-between; padding:12px 10px 0; font-weight:700; font-size:.88rem; color:#1F2937; }
        .detail-footer-3col { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; margin-top:18px; padding-top:16px; border-top:1px solid #F1F5F9; }
        .detail-footer-3col > div { background:#F8FAFC; border-radius:10px; padding:12px 14px; }
        .timeline { display:flex; flex-direction:column; gap:0; }
        .timeline-item { display:flex; gap:12px; padding:0 0 18px; position:relative; }
        .timeline-item:not(:last-child)::before { content:''; position:absolute; left:4px; top:14px; bottom:0; width:1.5px; background:#E5E7EB; }
        .timeline-dot { width:9px; height:9px; border-radius:50%; background:var(--azul); margin-top:4px; flex-shrink:0; }
        .timeline-desc { font-size:.85rem; font-weight:600; color:#1F2937; margin:0; display:flex; justify-content:space-between; gap:10px; }
        .timeline-valor { font-weight:700; }
        .timeline-meta { font-size:.75rem; color:#9CA3AF; margin:2px 0 0; }
        .vermelho { color:#DC2626; }
        .verde { color:#16A34A; }

        .modal-header { display:flex; justify-content:space-between; align-items:center; padding:20px 24px 0; }
        .modal-header h2 { font-size:1.1rem; font-weight:700; color:var(--azul); }
        .modal-close { background:none; border:none; cursor:pointer; color:#9CA3AF; }
        .modal-body { padding:20px 24px; }
        .modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid #F1F5F9; }
        .tipo-toggle { display:flex; gap:8px; }
        .tipo-btn { flex:1; padding:10px; border-radius:8px; border:1.5px solid #E5E7EB; background:#F8FAFC; font-weight:600; cursor:pointer; font-size:.9rem; transition:.15s; color:#6B7280; }
        .tipo-btn--entrada { background:#DCFCE7; border-color:#86EFAC; color:#16A34A; }
        .tipo-btn--saida   { background:#FEE2E2; border-color:#FCA5A5; color:#DC2626; }
        .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
        .form-group { display:flex; flex-direction:column; gap:4px; }
        .form-full { grid-column:1/-1; }
        .obrigatorio { color:#EF4444; font-size:.7rem; font-weight:700; margin-left:4px; text-transform:none; }
        .alert-moto { display:flex; align-items:center; gap:6px; background:#FEF3C7; color:#92400E; font-size:.8rem; padding:8px 12px; border-radius:8px; margin-bottom:8px; }

        @media(max-width:1100px){ .fin-charts-row{grid-template-columns:1fr;} .moto-grouped{grid-template-columns:1fr;} .fin-kpis-4{grid-template-columns:repeat(2,1fr);} }
        @media(max-width:640px){ .page{padding:16px;} .fin-kpis-4{grid-template-columns:1fr;} .form-grid{grid-template-columns:1fr;} .form-full{grid-column:1;} .fin-banner{padding:18px;} .detail-footer-3col{grid-template-columns:1fr;} }
      `}</style>
    </div>
  );
}
